/**
 * Module dependencies.
 */

import { Button, Typography } from '@mui/material';
import { Container } from 'components/atoms/container/container';
import { color } from 'styles/utils/colors';
import { css, keyframes } from '@emotion/react';
import { ifProp } from 'styled-tools';
import { navBarHeight } from 'utils/constants';
import styled from '@emotion/styled';

/**
 * Animation time.
 */

export const animationTime = '1s';

/**
 * Export `HomeCarouselWrapper` styled component.
 */

export const HomeCarouselWrapper = styled.div`
  background: linear-gradient(135deg, #0e1824 0%, #1a2a3a 100%);
  padding-top: ${navBarHeight}px;
  position: relative;
  clip-path: polygon(0% 0%, 100% 0%, 100% 90%, 0% 100%);
  overflow: hidden;

  @media (max-width: 768px) {
    clip-path: initial;
    padding-top: 0;
  }
`;

/**
 * `CarouselWrapper` styled component.
 */

export const CarouselWrapper = styled(Container)`
  position: relative;
  height: 80vh;
  position: relative;
  padding: 0;

  @media (max-width: 768px) {
    height: 100vh;
  }
`;

/**
 * Export `CarouselContainer` styled component.
 */

export const CarouselContainer = styled.div`
  padding: 0;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
`;

/**
 * `Image` styled component.
 */

export const Image = styled.img<{
  left: boolean;
  right: boolean;
  visible: boolean;
}>`
  left: 450px;
  height: 80vh;
  min-height: 900px;
  position: absolute;
  opacity: ${ifProp('visible', 1, 0)};
  transform: translateX(0);

  ${ifProp(
    'left',
    css`
      transform: translateX(0%);
    `,
  )}

  ${ifProp(
    'right',
    css`
      transform: translateX(0%);
      transition-delay: 1.2s;
    `,
  )}

  transition: all ${animationTime} ease-in-out;

  @media (max-width: 768px) {
    height: 100vh;
    min-height: 100vh;
    left: 0;
  }
`;

/**
 * Export `MobileOverlay` styled component.
 */

export const MobileOverlay = styled.div`
  @media (max-width: 768px) {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    height: 100vh;
    background: linear-gradient(
      to bottom,
      rgba(26, 42, 58, 1) 0%,
      rgba(26, 42, 58, 0.7) 10%,
      rgba(26, 42, 58, 0.5) 20%,
      rgba(26, 42, 58, 0.2) 50%,
      rgba(26, 42, 58, 0) 100%
    );
    pointer-events: none;
  }
`;

/**
 * Export `Category` styled component.
 */

export const Category = styled(Typography)`
  text-transform: uppercase;
  color: ${(props) => props.color};
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  display: inline-block;
  font-size: 18px;
  margin-bottom: 16px;
  transition: all ${animationTime} ease-in-out;
  transform-origin: center bottom;
  opacity: 0;
  transform: translateY(-40px) scale(1.2);

  @media (max-width: 768px) {
    font-size: 16px;
    text-shadow: 2px 1px 3px rgba(14, 24, 36, 1);
  }
`;

/**
 * Export `Title` styled component.
 */

export const Title = styled((props) => (
  <Typography {...props} variant={'h3'} />
))<{
  left: boolean;
  right: boolean;
  visible: boolean;
}>`
  color: white;
  font-weight: 600;
  font-size: 80px;
  text-transform: uppercase;
  font-family: 'Inter', sans-serif;
  text-shadow: 4px 2px 8px rgba(14, 24, 36, 1);
  transition: all ${animationTime} ease-in-out;
  opacity: 0;
  transition-delay: 0.25s;
  transform: scale(1.2);

  @media (max-width: 768px) {
    font-size: 30px;
  }
`;

/**
 * Export `Description` styled component.
 */

export const Description = styled(Typography)`
  color: ${color('gray400')};
  margin-top: 16px;
  max-width: 300px;
  transition: all ${animationTime} ease-in-out;
  opacity: 0;
  transition-delay: 0.5s;
  transform: translateY(40px) scale(1.2);

  @media (max-width: 768px) {
    color: ${color('gray300')};
    text-shadow: 2px 1px 3px rgba(14, 24, 36, 1);
  }
`;

/**
 * Export `Content` styled component.
 */

export const Content = styled.div`
  left: 5%;
  position: absolute;
  top: 20%;
  max-width: 500px;
  z-index: 998;
  width: 500px;
`;

/**
 * Export `CTAWrapper` styled component.
 */

export const CTAWrapper = styled.div`
  position: absolute;
  padding-top: 64px;
`;

/**
 * Fade in up keyframes.
 */

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(60px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * `CTAContactButton` styled component.
 */

export const WhiteContainedButton = styled(Button)`
  opacity: 0;
  animation: ${fadeInUp} ${animationTime} ease-in-out;
  animation-delay: 0.1s;
  animation-fill-mode: forwards;
  font-weight: 600;

  &.MuiButton-contained {
    background-color: white;
    color: #0e1824;

    &:hover {
      background-color: #f0f0f0;
    }
  }
`;
